import { OctagonMinus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { LOCALES } from "@/lib/format";
import { useLanguage } from "@/lib/i18n/language";
import { cn } from "@/lib/utils";

import { type BlockageLine, statusLabel } from "./utils";

interface BlockageTextProps {
  line: BlockageLine;
  /** The language its ask was written in: the item's. */
  language: string;
  /** Opens the item a blockage of an item waits on. */
  onOpenItem: (id: string) => void;
}

/**
 * A blockage's words: who, then what they need to do ("An: de prijslijst
 * bezorgen", "Een leverancier: ..."). An item it waits on is a button to that
 * item's details, with its status in brackets, and what needs to happen after
 * it when the blockage says so. The colour comes from around it.
 */
export function BlockageText({ line, language, onOpenItem }: BlockageTextProps) {
  const { t } = useTranslation();
  const dashboard = useLanguage();
  const waited = line.item;
  return (
    <>
      {waited ? (
        <>
          <button
            type="button"
            onClick={() => onOpenItem(waited.id)}
            className="text-foreground hover:text-primary focus-visible:ring-ring rounded-sm text-left font-medium hover:underline focus-visible:ring-2 focus-visible:outline-none"
          >
            {line.who}
          </button>{" "}
          <span className="text-muted">
            {t("roadmap.blockers.itemStatus", {
              status: statusLabel(waited.status).toLocaleLowerCase(LOCALES[dashboard]),
            })}
          </span>
        </>
      ) : (
        <span className="font-medium">{line.who}</span>
      )}
      {line.ask && (
        <>
          {t("roadmap.blockers.separator")}
          <span lang={language}>{line.ask}</span>
        </>
      )}
    </>
  );
}

/**
 * How a blockage's words wrap: between words, and inside a long word only
 * where its language hyphenates it (14 letters or more, 7 on either side),
 * so a narrow column never breaks "kraanbeschikbaarheid" just anywhere.
 */
export const BLOCKAGE_WRAP = "break-words hyphens-auto [hyphenate-limit-chars:14_7_7]";

/** The blockage symbol at the start of a line: decorative, with "Blokkade" for screen readers. */
export function BlockageSymbol({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <>
      <OctagonMinus aria-hidden="true" className={cn("text-muted shrink-0", className)} />
      <span className="sr-only">{t("roadmap.blockers.symbol")} </span>
    </>
  );
}
